import React, { useState, useEffect, useRef, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MapPin, RotateCcw, ZoomIn, ZoomOut, Info, MousePointer, Trash2, Check } from 'lucide-react';

const StoreMap = () => {
  const { user } = useContext(AuthContext);
  const [markers, setMarkers] = useState([]);
  const [stocktakes, setStocktakes] = useState([]);
  const [selectedStocktake, setSelectedStocktake] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [placementMode, setPlacementMode] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapAvailable, setMapAvailable] = useState(false);
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const mapContainerRef = useRef(null);
  const imageRef = useRef(null);
  const token = localStorage.getItem('token');

  const TOTAL_AREAS = 261;

  // Get the current store_id
  const getStoreId = () => {
    if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
      return localStorage.getItem('selectedStoreId') || user?.store_id;
    }
    return user?.store_id;
  };

  useEffect(() => {
    fetchData();
  }, [user?.store_id]);

  // Listen for store changes from the admin dropdown
  useEffect(() => {
    const handleStorageChange = () => {
      fetchData();
    };
    window.addEventListener('storage', handleStorageChange);
    // Also listen for custom event from store switcher
    window.addEventListener('storeChanged', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storeChanged', handleStorageChange);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const storeId = getStoreId();
      const storeParam = storeId ? `?store_id=${storeId}` : '';
      
      // Fetch stores to get current store info
      const storesRes = await fetch(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const storesData = await storesRes.json();
      setStores(storesData);
      
      // Find current store - try exact match first, then check if user is network admin with no selection
      let store = storesData.find(s => s.id === storeId);
      
      // If no store found and we have stores, use the first one for network admins
      if (!store && storesData.length > 0) {
        // For network admins without a selected store, show the first available store
        if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
          store = storesData[0];
          // Update localStorage so it persists
          localStorage.setItem('selectedStoreId', store.id);
        } else {
          // For other users, find their assigned store
          store = storesData.find(s => s.id === user?.store_id);
        }
      }
      
      setCurrentStore(store);
      
      // Check if store has a map uploaded
      const hasMap = store?.map_image_url ? true : false;
      setMapAvailable(hasMap);
      
      if (!hasMap) {
        setStocktakes([]);
        setMarkers([]);
        setSelectedStocktake(null);
        setLoading(false);
        return;
      }
      
      // Use the found store's ID for stocktakes query
      const effectiveStoreParam = store?.id ? `?store_id=${store.id}` : '';
      const stocktakesRes = await fetch(`${API}/stocktakes${effectiveStoreParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const stocktakesData = await stocktakesRes.json();
      setStocktakes(stocktakesData);
      
      if (stocktakesData.length > 0) {
        const activeStocktake = stocktakesData.find(st => st.status === 'in_progress') || stocktakesData[0];
        setSelectedStocktake(activeStocktake.id);
        await fetchMarkers(activeStocktake.id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMarkers = async (stocktakeId) => {
    try {
      const response = await fetch(`${API}/custom-map-areas?stocktake_id=${stocktakeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMarkers(data);
      }
    } catch (error) {
      console.error(error);
      // Load from localStorage as fallback
      const saved = localStorage.getItem(`map_markers_${stocktakeId}`);
      if (saved) {
        setMarkers(JSON.parse(saved));
      }
    }
  };

  const saveMarkers = async (newMarkers) => {
    // Save to localStorage immediately
    localStorage.setItem(`map_markers_${selectedStocktake}`, JSON.stringify(newMarkers));
    
    // Try to save to backend
    try {
      await fetch(`${API}/custom-map-areas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          stocktake_id: selectedStocktake, 
          areas: newMarkers 
        })
      });
    } catch (error) {
      console.error('Failed to save to backend', error);
    }
  };

  const getMousePosition = (e) => {
    if (!imageRef.current) return null;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x, y };
  };

  const handleMapClick = (e) => {
    if (!placementMode) return;
    
    const pos = getMousePosition(e);
    if (pos) {
      const newMarker = {
        id: `marker-${Date.now()}`,
        name: `Point ${markers.length + 1}`,
        x: pos.x,
        y: pos.y,
        status: 'completed',
        createdAt: new Date().toISOString()
      };
      
      const updatedMarkers = [...markers, newMarker];
      setMarkers(updatedMarkers);
      saveMarkers(updatedMarkers);
      toast.success(`Point ${markers.length + 1} placed`);
    }
  };

  const handleMarkerClick = (marker, e) => {
    e.stopPropagation();
    if (placementMode) return;
    setSelectedMarker(marker);
  };

  const deleteMarker = (markerId) => {
    const updatedMarkers = markers.filter(m => m.id !== markerId);
    setMarkers(updatedMarkers);
    saveMarkers(updatedMarkers);
    setSelectedMarker(null);
    toast.success('Point removed');
  };

  const openEditModal = (marker) => {
    setEditingMarker({ ...marker });
    setEditModalOpen(true);
  };

  const saveMarkerEdit = () => {
    if (!editingMarker) return;
    
    const updatedMarkers = markers.map(m => 
      m.id === editingMarker.id ? editingMarker : m
    );
    setMarkers(updatedMarkers);
    saveMarkers(updatedMarkers);
    setEditModalOpen(false);
    setEditingMarker(null);
    toast.success('Point updated');
  };

  const clearAllMarkers = () => {
    if (!window.confirm('Are you sure you want to clear all points? This cannot be undone.')) {
      return;
    }
    setMarkers([]);
    saveMarkers([]);
    setSelectedMarker(null);
    toast.success('All points cleared');
  };

  const getProgress = () => {
    const completed = markers.length;
    return { 
      completed, 
      total: TOTAL_AREAS, 
      percentage: Math.round((completed / TOTAL_AREAS) * 100) 
    };
  };

  const progress = getProgress();

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="store-map-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading store map...</p>
        </div>
      </div>
    );
  }

  // Map unavailable for this store
  if (!mapAvailable) {
    return (
      <div className="space-y-6" data-testid="store-map-unavailable">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MapPin className="w-8 h-8 text-slate-400" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Interactive Store Map</h1>
            <p className="text-slate-600">{currentStore?.name || 'Unknown Store'}</p>
          </div>
        </div>

        {/* Unavailable Message */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-amber-800 mb-2">Store Map Unavailable</h2>
          <p className="text-amber-700 mb-4">
            No store map has been uploaded for <strong>{currentStore?.name || 'this store'}</strong>.
          </p>
          <p className="text-amber-600 text-sm">
            Please contact a Network Administrator to upload a map for this store.
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-slate-700">About Store Maps</h4>
              <p className="text-sm text-slate-600 mt-1">
                Each store requires a custom floor plan to use the interactive map feature. 
                Network Administrators can upload store-specific maps in the Settings page.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="store-map-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="w-8 h-8 text-teal-600" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Interactive Store Map</h1>
            <p className="text-slate-600">Click to mark completed count areas</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-lg px-4 py-2">
            {progress.completed} / {progress.total} areas
          </Badge>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Placement Mode Toggle */}
          <Button
            onClick={() => setPlacementMode(!placementMode)}
            variant={placementMode ? 'default' : 'outline'}
            className={placementMode ? 'bg-teal-600 hover:bg-teal-700' : ''}
            data-testid="placement-mode-btn"
          >
            <MousePointer className="w-4 h-4 mr-2" />
            {placementMode ? 'Placing Points...' : 'Place Points'}
          </Button>
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 border-l pl-3 ml-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
              data-testid="zoom-out-btn"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium w-16 text-center">{Math.round(zoom * 100)}%</span>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setZoom(Math.min(3, zoom + 0.25))}
              data-testid="zoom-in-btn"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={clearAllMarkers}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid="clear-all-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
          <Button
            variant="outline"
            onClick={() => setZoom(1)}
            data-testid="reset-zoom-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset View
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Overall Progress</span>
          <span className="text-sm font-bold text-teal-600">{progress.completed} of {progress.total} areas marked</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-teal-500 to-teal-600 transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <p className="text-xs text-slate-500 mt-1">{progress.percentage}% complete</p>
      </div>

      {/* Map Container */}
      <div 
        ref={mapContainerRef}
        className={`relative bg-slate-100 rounded-xl border-2 overflow-auto ${
          placementMode ? 'border-teal-400 cursor-crosshair' : 'border-slate-200'
        }`}
        style={{ height: '600px' }}
        data-testid="map-container"
      >
        {placementMode && (
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
            Click on the map to place a point
          </div>
        )}
        
        <div 
          className="relative inline-block min-w-full"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'top left' }}
        >
          {/* Store Map Image */}
          <img
            ref={imageRef}
            src={currentStore?.map_image_url || '/store-map.png'}
            alt="Store Floor Plan"
            className="w-full h-auto select-none"
            onClick={handleMapClick}
            draggable={false}
            data-testid="store-map-image"
          />
          
          {/* Render Markers as Green Dots */}
          {markers.map((marker) => (
            <div
              key={marker.id}
              className={`absolute cursor-pointer transition-all duration-200 ${
                selectedMarker?.id === marker.id ? 'z-20' : 'z-10'
              }`}
              style={{
                left: `${marker.x}%`,
                top: `${marker.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
              onClick={(e) => handleMarkerClick(marker, e)}
              data-testid={`marker-${marker.id}`}
            >
              {/* Green Dot - Sized to fit in bays */}
              <div 
                className={`w-2.5 h-2.5 rounded-full bg-green-500 shadow ${
                  selectedMarker?.id === marker.id ? 'ring-2 ring-green-400 scale-150' : 'hover:scale-125'
                } transition-all duration-200`}
              />
              
              {/* Tooltip on hover */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 hover:opacity-100 pointer-events-none transition-opacity">
                <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                  {marker.name}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Marker Info Panel */}
      {selectedMarker && !placementMode && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">{selectedMarker.name}</h3>
                <p className="text-sm text-slate-500">
                  Position: ({Math.round(selectedMarker.x)}%, {Math.round(selectedMarker.y)}%)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditModal(selectedMarker)}
              >
                Rename
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => deleteMarker(selectedMarker.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedMarker(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-teal-50 rounded-xl border border-teal-200 p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-teal-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-teal-800">How to use</h4>
            <ul className="text-sm text-teal-700 mt-1 space-y-1">
              <li>1. Click "Place Points" to enable placement mode</li>
              <li>2. Click anywhere on the map to place a green marker</li>
              <li>3. Each marker counts as one completed area (out of {TOTAL_AREAS})</li>
              <li>4. Click on a marker to rename or delete it</li>
              <li>5. Use zoom controls to see the map in detail</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Point</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Point Name</Label>
              <Input
                value={editingMarker?.name || ''}
                onChange={(e) => setEditingMarker(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter point name"
                data-testid="edit-marker-name"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveMarkerEdit} className="bg-teal-600 hover:bg-teal-700">
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreMap;
